import { QB_EXPENSE_TYPE_LABELS, type QbExpenseChange, type QbExpenseChangeKind, type QbExpenseCheckResult, type QbExpensePlan } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Dialog, Loading, Panel, PanelHeader, formatCents, formatDate } from "./ui";

const KIND: Record<QbExpenseChangeKind, string> = { add: "New", update: "Changed", move: "Moved to another job", to_general: "Now a general expense", delete: "Deleted in QuickBooks" };
const ORDER: QbExpenseChangeKind[] = ["delete", "to_general", "move", "add", "update"];

/** "3 new, 1 deleted…" from a result. */
export function expenseResultText(r: QbExpenseCheckResult) {
  const parts = [
    [r.added, "brought in"],
    [r.updated, "updated"],
    [r.moved, "moved to another job"],
    [r.toGeneral, "now general expenses"],
    [r.deleted, "deleted"],
    [r.attachments, "attachments copied"],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, what]) => `${n} ${what}`);
  return parts.length ? parts.join(", ") : "No changes";
}

/**
 * "Check for changes" against QuickBooks's expenses (the company's, or one job's): shows what would
 * change and changes nothing until Apply. Only the changes shown are applied.
 */
export function ExpenseSyncDialog({ jobId, onClose }: { jobId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const preview = useMutation({ mutationFn: () => post<QbExpensePlan>("/api/integrations/quickbooks/expenses/preview", { jobId: jobId ?? null }) });
  const apply = useMutation({
    mutationFn: (keys: string[]) => post<QbExpenseCheckResult>("/api/integrations/quickbooks/expenses/apply", { jobId: jobId ?? null, keys }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["receipts"] });
      await qc.invalidateQueries({ queryKey: ["qb-held"] });
    },
  });
  useEffect(() => preview.mutate(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const plan = preview.data;
  const changes = plan?.changes ?? [];
  const deletes = changes.filter((c) => c.kind === "delete").length;
  return (
    <Dialog
      wide
      title={jobId ? "Refresh from QuickBooks" : "Expenses from QuickBooks"}
      onClose={onClose}
      footer={
        apply.data ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!changes.length} busy={apply.isPending} onClick={() => apply.mutate(changes.map((c) => c.key))}>
              {changes.length ? `Apply ${changes.length} ${changes.length === 1 ? "change" : "changes"}` : "Nothing to apply"}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4 text-sm">
        {(preview.error || apply.error) && <Alert>{errorMessage(preview.error ?? apply.error)}</Alert>}
        {preview.isPending ? (
          <div className="flex items-center gap-3 text-muted">
            <Loading /> Reading QuickBooks…
          </div>
        ) : apply.data ? (
          <Alert tone="ok">Done: {expenseResultText(apply.data)}.</Alert>
        ) : plan ? (
          <>
            {!changes.length ? (
              <p className="text-ink-soft">Everything matches QuickBooks. Nothing to change.</p>
            ) : (
              <p className="text-ink-soft">
                These are the changes from QuickBooks. Nothing changes until you apply them.
                {deletes > 0 && ` ${deletes} ${deletes === 1 ? "receipt goes" : "receipts go"} to the recycle bin (a file uploaded here stays in the job's files).`}
              </p>
            )}
            {ORDER.map((k) => {
              const list = changes.filter((c) => c.kind === k);
              return list.length ? <ChangeList key={k} title={`${KIND[k]} (${list.length})`} list={list} /> : null;
            })}
            {plan.attention.length > 0 && (
              <div>
                <div className="mb-1 font-semibold text-wait">Needs attention ({plan.attention.length})</div>
                <p className="mb-2 text-muted">{plan.attention[0]!.note}</p>
                <ul className="divide-y divide-mist rounded border border-line">
                  {plan.attention.map((a) => (
                    <li key={`${a.qbType}:${a.qbId}`} className="flex flex-wrap gap-x-3 px-3 py-2">
                      <span className="font-semibold">{a.vendorName || QB_EXPENSE_TYPE_LABELS[a.qbType]}</span>
                      <span className="text-muted">{[QB_EXPENSE_TYPE_LABELS[a.qbType], a.date && formatDate(a.date), a.job && `on ${a.job} here`].filter(Boolean).join(" · ")}</span>
                      <span className="ml-auto">{formatCents(a.amountCents)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

function ChangeList({ title, list }: { title: string; list: QbExpenseChange[] }) {
  return (
    <div>
      <div className="mb-1 font-semibold">{title}</div>
      <ul className="divide-y divide-mist rounded border border-line">
        {list.map((c) => (
          <li key={c.key} className="flex flex-wrap gap-x-3 px-3 py-2">
            <span className="font-semibold">{c.vendorName || QB_EXPENSE_TYPE_LABELS[c.qbType]}</span>
            <span className="text-muted">
              {[QB_EXPENSE_TYPE_LABELS[c.qbType], c.date && formatDate(c.date), c.source === "crm" && "entered here", c.kind === "move" ? `${c.fromJob} → ${c.toJob}` : c.kind === "to_general" ? `off ${c.fromJob}` : (c.toJob ?? c.fromJob) || (c.kind === "add" ? "general expense" : null)].filter(Boolean).join(" · ")}
            </span>
            <span className="ml-auto">{formatCents(c.amountCents)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type Held = { id: string; vendorName: string; date: string | null; amountCents: number; source: string; qbType: string | null; job: string | null };

/** Deletions the automatic sync held for approval (more than it deletes on its own at once): delete them here too, or keep them. */
export function HeldExpenses() {
  const qc = useQueryClient();
  const held = useQuery({ queryKey: ["qb-held"], queryFn: () => get<Held[]>("/api/integrations/quickbooks/expenses/held") });
  const decide = useMutation({
    mutationFn: (action: "delete" | "keep") => post("/api/integrations/quickbooks/expenses/held", { ids: held.data!.map((h) => h.id), action }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["qb-held"] });
      await qc.invalidateQueries({ queryKey: ["receipts"] });
      await qc.invalidateQueries({ queryKey: ["sync"] });
    },
  });
  if (!held.data?.length) return null;
  return (
    <Panel>
      <PanelHeader title={`Deleted in QuickBooks, held for you (${held.data.length})`} description="The automatic sync found these receipts gone from QuickBooks, more than it deletes on its own at once. Delete them here too (to the recycle bin), or keep them." />
      <div className="flex flex-col gap-2 px-5 py-4 text-sm">
        <ul className="max-h-64 divide-y divide-mist overflow-y-auto rounded border border-line">
          {held.data.map((h) => (
            <li key={h.id} className="flex flex-wrap gap-x-3 px-3 py-2">
              <span className="font-semibold">{h.vendorName || "Receipt"}</span>
              <span className="text-muted">{[h.date && formatDate(h.date), h.job, h.source === "crm" && "entered here"].filter(Boolean).join(" · ")}</span>
              <span className="ml-auto">{formatCents(h.amountCents)}</span>
            </li>
          ))}
        </ul>
        {decide.error && <Alert>{errorMessage(decide.error)}</Alert>}
        <div className="flex gap-2">
          <Button variant="primary" busy={decide.isPending && decide.variables === "delete"} onClick={() => window.confirm(`Delete these ${held.data!.length} receipts?`) && decide.mutate("delete")}>
            Delete them
          </Button>
          <Button busy={decide.isPending && decide.variables === "keep"} onClick={() => decide.mutate("keep")}>
            Keep them
          </Button>
        </div>
      </div>
    </Panel>
  );
}
